import React, { useCallback, useMemo, useState } from 'react';
import SectionsCard from '../interaction/SectionsCard';
import ColumnLabel from '../interaction/ColumnLabel';
import Pill from '../interaction/Pill';
import SelectWithSearch from '../interaction/SelectWithSearch';
import DebouncedTextInput from '../interaction/DebouncedTextInput';
import { UserRolesSummary, UserRolesCatalog } from './UserRolesRow';
import { getFrequentRoles, forgetRole } from '../utils/roleFrequency';
import './UserFormCard.css';

// Email / display name / company across the top row. Roles get a row of their own
// below, full width, so the pill grid and its "Quick add" shortcuts have room.
const FIELD_WIDTHS = 'minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr)';

function countLabel(n, singular) {
  return `${n} ${n === 1 ? singular : `${singular}s`}`;
}

function saveButtonLabel(drafts, saving) {
  if (saving) return 'Saving…';
  const creates = drafts.filter(d => d.mode === 'create').length;
  const edits = drafts.length - creates;
  if (creates && edits) return `Save ${countLabel(creates, 'new user')} + ${countLabel(edits, 'edit')}`;
  if (edits) return `Save ${countLabel(edits, 'edit')}`;
  return `Save ${countLabel(creates, 'user')}`;
}

/**
 * The /users add-and-edit form: one card per user, each holding a row of fields
 * and a row of roles.
 *
 * Every card is the same form. A 'create' draft becomes a createUser call, an
 * 'edit' draft (pushed here by the Edit action on a row) becomes an updateUser
 * call. Saving walks the drafts one at a time — this is UI scaffolding for
 * entering several users at once, not a bulk endpoint.
 */
export default function UserFormCard({
  drafts,
  companies,
  roles,
  onRemove,
  onChange,
}) {
  // Collapsed cards are tracked here rather than inside SectionsCard: one header
  // toggle has to fold away both of the card's sections, not just its own.
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapsed = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // Roles folds on its own, and is tracked here rather than inside SectionsCard so
  // that folding the whole card and reopening it does not forget the choice.
  const [rolesCollapsed, setRolesCollapsed] = useState({});
  const toggleRolesCollapsed = (key) => setRolesCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // The catalog is the long one, so it starts folded — absent key means closed.
  const [catalogOpen, setCatalogOpen] = useState({});
  const toggleCatalogOpen = (key) => setCatalogOpen(prev => ({ ...prev, [key]: !prev[key] }));

  // The shortcut list lives in localStorage, so dropping one needs an explicit
  // nudge to re-render — there is no state for it to invalidate.
  const [shortcutsVersion, setShortcutsVersion] = useState(0);
  const dropShortcut = useCallback((roleId) => {
    forgetRole(roleId);
    setShortcutsVersion(v => v + 1);
  }, []);

  const companyOptions = useMemo(
    () => [...companies]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(company => ({ value: company.id, label: company.name })),
    [companies]
  );

  // Read once per render of this component rather than once per draft: it parses
  // JSON out of localStorage. shortcutsVersion re-reads it after an X.
  const frequentRoles = useMemo(
    () => getFrequentRoles(roles),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles, shortcutsVersion]
  );

  // Stable identities, so the memoised roles row is not invalidated on every keystroke.
  const handleRolesChange = useCallback(
    (key, nextRoles) => onChange(key, { roles: nextRoles }),
    [onChange]
  );
  const handleFieldChange = useCallback(
    (key, field, nextValue) => onChange(key, { [field]: nextValue }),
    [onChange]
  );

  const sectionsFor = (draft, createIndex) => {
    const isEdit = draft.mode === 'edit';
    const errors = draft.errors || {};
    const isOpen = !collapsed[draft.key];

    const emailColumn = (
      <div className="uf-field">
        {draft.serverError && <p className="uf-section-error">{draft.serverError}</p>}
        <ColumnLabel>Email{isEdit ? '' : ' *'}</ColumnLabel>
        <DebouncedTextInput
          className={`uf-input${errors.email ? ' uf-input--error' : ''}`}
          type="email"
          value={draft.email}
          disabled={isEdit}
          placeholder="name@company.com"
          // The field is greyed out on edit, so the reason lives on hover rather
          // than as another line of helper text under it.
          title={isEdit ? 'Only system admins can change an email address.' : undefined}
          onValueChange={(next) => handleFieldChange(draft.key, 'email', next)}
        />
        {errors.email && <p className="uf-hint uf-hint--error">{errors.email}</p>}
      </div>
    );

    const displayNameColumn = (
      <div className="uf-field">
        <ColumnLabel>Display name</ColumnLabel>
        <DebouncedTextInput
          className="uf-input"
          type="text"
          value={draft.displayName}
          placeholder="Optional"
          onValueChange={(next) => handleFieldChange(draft.key, 'displayName', next)}
        />
      </div>
    );

    const companyColumn = (
      <div className="uf-field">
        <ColumnLabel>Company{isEdit ? '' : ' *'}</ColumnLabel>
        {companies.length === 0 ? (
          <p className="uf-hint uf-hint--error">
            No companies exist yet — create one under Companies, then come back here.
          </p>
        ) : (
          <>
            <SelectWithSearch
              options={companyOptions}
              selectedValues={draft.company ? [draft.company] : []}
              onSelectionChange={([value]) => onChange(draft.key, { company: value || '' })}
              placeholder="Select a company…"
              invalid={!!errors.company}
              // Companies are typed, not scrolled for — always offer the search box.
              numberOptionsToShowSearch={0}
            />
            {errors.company && <p className="uf-hint uf-hint--error">{errors.company}</p>}
          </>
        )}
      </div>
    );

    const rolesSummaryColumn = (
      <UserRolesSummary
        draftKey={draft.key}
        hasRoles={roles.length > 0}
        selectedRoles={draft.roles}
        frequentRoles={frequentRoles}
        error={errors.roles}
        onRolesChange={handleRolesChange}
        onDropShortcut={dropShortcut}
      />
    );

    const rolesCatalogColumn = (
      <UserRolesCatalog
        draftKey={draft.key}
        roles={roles}
        selectedRoles={draft.roles}
        onRolesChange={handleRolesChange}
      />
    );

    const fieldsSection = {
      key: `${draft.key}-fields`,
      title: isEdit ? `Editing ${draft.email}` : `New user ${createIndex}`,
      collapsible: true,
      isOpen,
      onToggle: () => toggleCollapsed(draft.key),
      columnWidths: FIELD_WIDTHS,
      columns: [emailColumn, displayNameColumn, companyColumn],
      headerAction: (
        <div className="uf-section-actions">
          {isEdit && <Pill label="editing" variant="light" size="xs" className="pill--status-warning" />}
          {draft.serverError && <Pill label="failed" variant="light" size="xs" className="pill--status-error" />}
          <button
            type="button"
            className="uf-icon-btn"
            aria-label={isEdit ? `Stop editing ${draft.email}` : `Remove new user ${createIndex}`}
            title={isEdit ? 'Stop editing' : 'Remove'}
            onClick={() => onRemove(draft.key)}
          >
            ×
          </button>
        </div>
      ),
    };

    const rolesSection = {
      key: `${draft.key}-roles`,
      title: 'Roles *',
      collapsible: true,
      isOpen: !rolesCollapsed[draft.key], // expanded by default
      onToggle: () => toggleRolesCollapsed(draft.key),
      columns: [rolesSummaryColumn],
    };

    const catalogSection = {
      key: `${draft.key}-catalog`,
      title: 'Full roles catalog',
      collapsible: true,
      isOpen: !!catalogOpen[draft.key], // collapsed by default
      onToggle: () => toggleCatalogOpen(draft.key),
      columns: [rolesCatalogColumn],
    };

    // Collapsing the card hides the roles sections too, not just the fields.
    if (!isOpen) return [fieldsSection];
    return roles.length > 0
      ? [fieldsSection, rolesSection, catalogSection]
      : [fieldsSection, rolesSection];
  };

  let createIndex = 0;

  // Nothing queued: the page is just the users list. The button that opens the
  // form lives in the page header's slot, not here.
  if (drafts.length === 0) return null;

  return (
    <div className="uf">
      <div className="uf-users">
        {drafts.map(draft => {
          if (draft.mode === 'create') createIndex += 1;
          return (
            <SectionsCard key={draft.key} sections={sectionsFor(draft, createIndex)} />
          );
        })}
      </div>
    </div>
  );
}

/**
 * The form's buttons, rendered apart from the form itself so they can sit in the
 * page header's slot — the standard place for a page's primary action. Closed,
 * that is a single "Add User"; open, it is "Add another user" next to Save.
 */
export function UserFormActions({ drafts, saving, onAdd, onSave }) {
  if (drafts.length === 0) {
    return (
      <button type="button" className="uf-btn uf-btn--primary" onClick={onAdd}>
        + Add User
      </button>
    );
  }

  return (
    <div className="uf-bar__actions">
      <button type="button" className="uf-btn uf-btn--ghost" onClick={onAdd}>
        + Add another user
      </button>
      <button
        type="button"
        className="uf-btn uf-btn--primary"
        onClick={onSave}
        disabled={saving}
      >
        {saveButtonLabel(drafts, saving)}
      </button>
    </div>
  );
}
